import React, { useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { CmsEntities, bindToContent } from 'client/data/models/cms';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { Container, Row, Col } from 'reactstrap';
import { AFFORDABILITY_BREADCRUMBS } from 'site-modules/calculators/constants/calculators';
import { CalculatorHeader } from 'site-modules/calculators/components/calculator-header/calculator-header';
import { AffordabilityCalculator } from 'site-modules/calculators/components/affordability-calculator/affordability-calculator';
import { CalculatorsArticles } from 'site-modules/calculators/components/calculators-articles/calculators-articles';
import { transformContentForFaq } from 'site-modules/shared/components/faq/utils';
import { WRAPPER_TYPES } from 'site-modules/shared/components/faq/constants';
import { Faq } from 'site-modules/shared/components/faq/faq';
import { HomepageAdExtensionMap } from 'client/tracking/maps/common/homepage-ad-extension';
import { CalculatorMap } from 'client/tracking/maps/calculator';
import { AppraisalMap } from 'client/tracking/maps/appraisal';
import { TrackingHandler } from 'client/tracking/handler';
import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';
import { HomeMarquee } from 'site-modules/shared/components/upper-funnel/home-marquee/home-marquee';
import { getQuery } from 'client/utils/location';
import { getIsCarmaxTraffic } from 'site-modules/calculators/utils/affordability-calculator-utils';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { CarmaxCalculatorHeader } from 'site-modules/calculators/components/carmax-calculator-header/carmax-calculator-header';
import { CarEquityModule } from 'site-modules/calculators/components/car-equity-module/car-equity-module';

import './calculators-affordability.scss';

export const CALCULATORS_AFFORDABILITY_CONTENT_FILE_PATH = 'calculators/affordability';

const CalculatorsAffordabilityUI = ({ location = {}, price = null, content = DEFAULT_CONTENT }) => {
  const isCarmaxTraffic = getIsCarmaxTraffic(location);
  const titleHeader = content?.title ?? 'Car Affordability Calculator: How Much Car Can I Afford?';
  const faqContent = content.child('faq');
  const articlesContent = content.child('articles');
  const articleTitle = articlesContent.title;
  const articleAuthor = articlesContent.metadata('author').value();
  const articleDate = articlesContent.metadata('date').value();
  const articles = articlesContent.children();
  const subheader = 'How much car can you afford? Find out with Edmunds Auto Affordability Calculator.';
  const { vin } = getQuery(location);

  useEffect(() => {
    TrackingHandler.useMap({ ...CalculatorMap, ...HomepageAdExtensionMap, ...AppraisalMap });
  }, []);

  if (isCarmaxTraffic) {
    return (
      <main tag="main" id="main-content" tabIndex={-1} className="pb-3">
        <CarmaxCalculatorHeader />
        <CarEquityModule vin={vin} />
        <Container className="pt-md-3">
          <Row className="justify-content-center">
            <Col xs={12} sm={10} lg={8}>
              <ScrollElement id="affortability-calculator" className="calculators-affordability-page">
                <Fragment>
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <div className="gradient-line-1 w-100 hidden-md-down me-1" />
                    <div className="gradient-text fw-bold text-uppercase text-nowrap">AFFORDABILITY CALCULATOR</div>
                    <div className="gradient-line-2 w-100 hidden-md-down ms-1" />
                  </div>
                  <h2 className="carmax-traffic-header text-center mb-0_75">
                    Cars you&apos;ll want. Payments you&apos;ll love.
                  </h2>
                  <p className="text-center size-20">
                    Calculate a monthly payment you want with the equity you may have and upgrade your car.
                  </p>
                </Fragment>
                <AffordabilityCalculator monthlyPayment={price} isCarmaxTraffic={isCarmaxTraffic} />
              </ScrollElement>
            </Col>
          </Row>
        </Container>
      </main>
    );
  }

  return (
    <Container tag="main" id="main-content" tabIndex={-1}>
      {!isCarmaxTraffic && (
        <CalculatorHeader
          breadcrumbs={AFFORDABILITY_BREADCRUMBS}
          header={titleHeader}
          subheader={subheader}
          isHeroTitle
          hideSeparator
        />
      )}
      <Row>
        <Col xs={12} lg={8} className="mb-2">
          <AffordabilityCalculator monthlyPayment={price} isCarmaxTraffic={isCarmaxTraffic} />
          <hr className="mt-2 mb-1_5" />
          <CalculatorsArticles
            articles={articles}
            title={articleTitle}
            author={articleAuthor}
            date={articleDate}
            className="mt-1"
          />
          <Faq
            {...transformContentForFaq(faqContent)}
            wrapperType={WRAPPER_TYPES.accordion}
            coloredLinks
            pathname={location.pathname}
            generateLearnMoreLinks
          />
        </Col>
        <ErrorBoundary>
          <Col xs={12} lg={8}>
            <hr className="mt-2 mb-1_5" />
            <HomeMarquee />
          </Col>
        </ErrorBoundary>
      </Row>
    </Container>
  );
};

CalculatorsAffordabilityUI.propTypes = {
  location: PropTypes.shape({}),
  price: PropTypes.number,
  content: CmsEntities.Content,
};

export const CalculatorsAffordability = connectToModel(CalculatorsAffordabilityUI, {
  content: bindToContent(CALCULATORS_AFFORDABILITY_CONTENT_FILE_PATH),
});
